import { FC, useCallback, useEffect, useState } from 'react';
import { SignUpFlowPage } from '../SignUpFlowPage/SignUpFlowPage';
import { httpClient } from '../../services/httpClient/httpClient';
import { usePageVisibility } from 'react-page-visibility';
import { useAppDispatch, useTypedSelector } from '../../store/store';
import { getUserRequest } from '../../store/slices/me';
import { getCampaignById, getPrimaryCampaign, setCurrentCampaign } from '../../store/slices/rewards';
import { CampaignApiModel } from '../../api/models/rewards';
import { useRouter } from 'next/router';
import { useBackgroundUploadSpeedTest } from '../../common/hooks/useBackgroundUploadSpeedTest';
import { getCachedUploadSpeed } from '../VideoToolPage/utils';
import { resetShareStory } from '../../store/slices/stories';
import { ExpiredLinkScreen } from './DeactivatedScreen';
import { SpeedTestContainer, MetricBlock, TestMetricBlock, MetricLabel } from './HomePage.styles';

// The campaign fetch used to be held to a 2 s minimum so the curtain intro
// had time to play. The intro is gone; the ticket is first paint and only
// the upload's auth step waits for the campaign, so resolve immediately.
const DEFAULT_WAIT_TIME = 0;

export const HomePage: FC = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const {
    account: { id: accountId, subdomain, shortcode, active, isLoading: isAccountLoading },
    accountAttributes: { isLoading: isAccountPropertiesLoading },
  } = useTypedSelector((state) => state.account);
  const queryShortCode = router.query['cid'] as string;
  const associationId = router.query['associationId'] as string;
  const isVisible = usePageVisibility();
  const { uploadMetrics } = useTypedSelector((state) => state.ui);

  // Get the slug from URL if we're in slug-based routing
  const currentSlug = router.query['slug'] as string;

  const [isLoading, setIsLoading] = useState(true);

  const removeCidParameter = useCallback(() => {
    const { cid, ...restQuery } = router.query;
    router.replace(
      {
        pathname: router.pathname,
        query: restQuery,
      },
      undefined,
      { shallow: true },
    );
  }, [router]);

  const { speedMbps, loading } = useBackgroundUploadSpeedTest({
    sizeInBytes: 5 * 1024 * 1024,
    rounds: 3,
  });
  const uploadSpeed = typeof window != 'undefined' ? getCachedUploadSpeed() : null;

  useEffect(() => {
    dispatch(resetShareStory());
  }, [dispatch]);

  useEffect(() => {
    if (!loading && speedMbps !== null) {
      sessionStorage.setItem('uploadSpeedMbps', speedMbps.toString());
    }
  }, [loading, speedMbps]);

  useEffect(() => {
    // needed for auto logout on tab activation when token is expired
    const userId = httpClient.getUserId();
    if (isVisible && userId) {
      dispatch(getUserRequest(userId));
    }
  }, [dispatch, isVisible]);

  useEffect(() => {
    // Don't run campaign logic until router is ready and account is loaded
    if (!router.isReady || isAccountLoading || !accountId) {
      return;
    }

    // IMPORTANT: If we're in slug-based routing, make sure the loaded account matches the slug
    // This prevents using a stale/persisted account from a previous session
    if (currentSlug && shortcode !== currentSlug && subdomain !== currentSlug) {
      return;
    }

    let timer: ReturnType<typeof setTimeout>;
    const startTime = new Date().getTime();
    setIsLoading(true);

    const currentCampaignShortCode = queryShortCode || localStorage.getItem('campaignId');
    const currentAssociationId = associationId || localStorage.getItem('associationId');

    if (currentAssociationId) {
      localStorage.setItem('associationId', currentAssociationId);
    }

    if (currentCampaignShortCode) {
      dispatch(
        getCampaignById({
          id: currentCampaignShortCode,
        }),
      ).then((data) => {
        const campaign = data.payload as CampaignApiModel;

        // If campaign is valid and matches the current account, use it
        if (!campaign?.endedAt && campaign?.accountId === accountId) {
          dispatch(setCurrentCampaign(campaign));
          if (campaign?.id) {
            localStorage.setItem('campaignId', campaign?.id);
          } else {
            localStorage.removeItem('campaignId');
          }

          const elapsed = new Date().getTime() - startTime;
          timer = setTimeout(() => setIsLoading(false), Math.max(DEFAULT_WAIT_TIME - elapsed, 0));
        } else {
          // Campaign doesn't match - DON'T remove cid from URL, just fetch primary campaign
          // The cid might be valid after account loads properly, or user might be sharing across accounts
          localStorage.removeItem('campaignId');

          if (accountId) {
            dispatch(
              getPrimaryCampaign({
                accountId,
              }),
            ).then((data) => {
              const campaign = data.payload as CampaignApiModel;
              dispatch(setCurrentCampaign(campaign));
              localStorage.setItem('campaignId', campaign?.id);
              const elapsed = new Date().getTime() - startTime;
              timer = setTimeout(() => setIsLoading(false), Math.max(DEFAULT_WAIT_TIME - elapsed, 0));
            });
          }
        }
      });
    } else if (accountId) {
      dispatch(
        getPrimaryCampaign({
          accountId,
        }),
      ).then((data) => {
        const campaign = data.payload as CampaignApiModel;
        dispatch(setCurrentCampaign(campaign));

        const elapsed = new Date().getTime() - startTime;
        timer = setTimeout(() => setIsLoading(false), Math.max(DEFAULT_WAIT_TIME - elapsed, 0));
      });
    }

    return () => clearTimeout(timer);
  }, [
    queryShortCode,
    dispatch,
    accountId,
    associationId,
    isAccountLoading,
    router.isReady,
    removeCidParameter,
    currentSlug,
    shortcode,
    subdomain,
  ]); // Added slug matching to prevent stale account

  const isWindow = typeof window !== 'undefined';
  if (!isWindow) {
    return <div></div>;
  }

  const app_env = process.env.APP_ENV || '';
  const testEnvs = ['development', 'staging'];

  if (isAccountLoading || isAccountPropertiesLoading) {
    return <></>;
  }

  if (!isAccountLoading && !active) {
    return <ExpiredLinkScreen />;
  }

  return (
    <>
      {testEnvs.includes(app_env) && (
        <SpeedTestContainer id="speed-test-result-block" style={{ display: 'none' }}>
          <MetricBlock>
            <MetricLabel>UPLOAD</MetricLabel>: [S] {uploadMetrics.speedMbs.toFixed(2) || '0'} <span> mbps </span>
            <span>
              [C] {uploadMetrics?.chunkSize ? uploadMetrics?.chunkSize / 1024 / 1024 : '0'} <span> MB</span>
            </span>
          </MetricBlock>
          <TestMetricBlock>
            {/* The landing reading no longer sizes any part (every upload opens
                at 1 MiB), so this row shows the speed only. */}
            <MetricLabel>TEST</MetricLabel>: [S] {uploadSpeed?.speedMbps.toFixed(2) || '0'} <span> mbps </span>
          </TestMetricBlock>
        </SpeedTestContainer>
      )}
      <SignUpFlowPage isDataLoading={isLoading} />
    </>
  );
};
